import { useState } from 'react';
import { seatLabel } from '@bre-ticketing/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { ApiError } from '../api';
import { groupSalesEndpoints } from '../api-group-sales';
import type { GroupHoldDetail } from '../api-group-sales';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars } from './event/shared';
import { urgency } from './GroupHolds';

/**
 * One group hold (D-336): what the group has, what it owes, and what staff do
 * next: move the deadline, send the link again, give back the seats they no
 * longer need, or hold more for the same group.
 */

const TONE: Record<string, Tone> = { HELD: 'warn', PAID: 'ok', RELEASED: 'neutral', CANCELLED: 'neutral' };
const REMINDER: Record<string, string> = { DAYS_BEFORE_DUE_7: 'A week before', DAYS_BEFORE_DUE_1: 'The day before', DAYS_AFTER_DUE_1: 'The day after' };

/** The local day of an instant, for the date input. */
const ymdOf = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function GroupHoldDetailPage() {
  const { id = '' } = useParams();
  const can = usePermissions();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(((location.state as { link?: string | null } | null)?.link ?? null) || null);
  const detail = useQuery<GroupHoldDetail, ApiError>({ queryKey: ['group-hold', id], queryFn: () => groupSalesEndpoints.detail(id) });

  if (!can('group_sales.view')) return <NotPermitted permission="group_sales.view" />;
  if (detail.error) return <ErrorNote>{detail.error.message}</ErrorNote>;
  if (!detail.data) return <p className="text-sm text-ink-500">Loading…</p>;

  const { group_hold: hold, order, performance, seats, payment_requests: requests, reminders } = detail.data;
  const open = hold.status === 'HELD';
  const manage = can('group_sales.manage') && open;
  const due = urgency(order.payment_due_at, hold.status);
  const liveSeats = seats.filter((seat) => seat.status === 'ACTIVE' || seat.status === 'PENDING');

  return (
    <div className="space-y-4">
      <p className="text-sm">
        <Link to="/group-holds" className="text-live hover:underline">
          ← Group holds
        </Link>
      </p>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {link ? (
        <div className="space-y-2 rounded-lg border border-line bg-surface px-5 py-4">
          <p className="text-sm font-medium">Payment link</p>
          <input readOnly value={link} className={`w-full font-mono text-xs ${INPUT}`} onFocus={(e) => e.currentTarget.select()} aria-label="Payment link" />
          <div className="flex gap-2">
            <Button onClick={() => void navigator.clipboard?.writeText(link)}>Copy</Button>
            <Button onClick={() => setLink(null)}>Done</Button>
          </div>
        </div>
      ) : null}

      <Panel
        title={hold.organization_name}
        description={`${performance.event_title} · ${formatWhen(performance.starts_at)} · ${performance.venue_name}`}
        actions={
          <div className="flex items-center gap-2">
            {/* For the group's purchase order (D-351); a released hold has nothing to invoice. */}
            {hold.status === 'HELD' || hold.status === 'PAID' ? (
              <Button onClick={() => window.open(groupSalesEndpoints.invoiceUrl(hold.id), '_blank', 'noopener')}>Download invoice (PDF)</Button>
            ) : null}
            <Badge tone={TONE[hold.status] ?? 'neutral'}>{hold.status.toLowerCase()}</Badge>
            <Link to={`/orders/${hold.order_id}`} className="font-mono text-sm text-live hover:underline">
              {order.order_number}
            </Link>
          </div>
        }
      >
        <div className="grid gap-6 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Contact">
            {hold.contact_name}
            <div className="text-xs text-ink-500">
              {hold.contact_email}
              {hold.contact_phone ? ` · ${hold.contact_phone}` : ''}
            </div>
            {hold.organization_id ? (
              <Link to={`/organizations?id=${hold.organization_id}`} className="text-xs text-live hover:underline">
                Organization record
              </Link>
            ) : null}
          </Fact>
          <Fact label="Seats">
            <span className="tabular text-2xl font-semibold">{hold.seat_count}</span>
          </Fact>
          <Fact label="Owed">
            <span className="tabular text-2xl font-semibold">{dollars(order.balance_due_cents)}</span>
            <div className="text-xs text-ink-500">
              {dollars(order.paid_cents)} paid of {dollars(order.total_cents)}
            </div>
          </Fact>
          <Fact label="Pay by">
            {order.payment_due_at ? formatWhen(order.payment_due_at) : '—'}
            <div className="flex flex-wrap items-center gap-2">
              {due ? <Badge tone={due.tone}>{due.text}</Badge> : null}
              {order.grace_hours !== null && open ? <span className="text-xs text-ink-500">released {order.grace_hours}h after</span> : null}
            </div>
          </Fact>
        </div>
        {hold.note ? <p className="border-t border-line px-5 py-3 text-sm text-ink-600">{hold.note}</p> : null}
        {hold.release_reason ? <p className="border-t border-line px-5 py-3 text-sm text-ink-600">Released: {hold.release_reason}</p> : null}
      </Panel>

      {manage ? <Actions id={id} dueAt={order.payment_due_at} graceHours={order.grace_hours} contactName={hold.contact_name} paid={order.paid_cents > 0} onLink={setLink} onError={setError} /> : null}

      <SeatsPanel id={id} seats={seats} liveCount={liveSeats.length} canGiveBack={manage && order.paid_cents === 0} onError={setError} />

      <Panel title="Payment link" description="What was sent, and when the reminders go.">
        <Table headers={['Link', 'Sent', 'Opened', 'Due']}>
          {requests.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-5 py-3 text-sm text-ink-500">
                No link yet.
              </td>
            </tr>
          ) : (
            requests.map((request) => (
              <tr key={request.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm">
                  <Badge tone={request.status === 'ACTIVE' ? 'ok' : request.status === 'PAID' ? 'ok' : 'neutral'}>{request.status.toLowerCase().replaceAll('_', ' ')}</Badge>
                  <div className="text-xs text-ink-500">{request.recipient_email}</div>
                </td>
                <td className="px-5 py-3 text-sm">
                  {request.sent_at ? formatWhen(request.sent_at) : 'Not emailed'}
                  {request.send_count > 1 ? <div className="text-xs text-ink-500">{request.send_count} times</div> : null}
                </td>
                <td className="px-5 py-3 text-sm">{request.last_viewed_at ? formatWhen(request.last_viewed_at) : '—'}</td>
                <td className="px-5 py-3 text-sm">{request.due_at ? formatWhen(request.due_at) : '—'}</td>
              </tr>
            ))
          )}
        </Table>
        {reminders.length > 0 ? (
          <ul className="space-y-1 border-t border-line px-5 py-3 text-sm">
            {reminders.map((reminder) => (
              <li key={reminder.offset_code} className="flex flex-wrap gap-2">
                <span className="w-32 text-ink-500">{REMINDER[reminder.offset_code] ?? reminder.offset_code}</span>
                {reminder.sent_at ? <span>Sent {formatWhen(reminder.sent_at)}</span> : <span>Goes {formatWhen(reminder.scheduled_for)}</span>}
              </li>
            ))}
          </ul>
        ) : null}
      </Panel>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-sm">
      <div className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Actions({ id, dueAt, graceHours, contactName, paid, onLink, onError }: { id: string; dueAt: string | null; graceHours: number | null; contactName: string; paid: boolean; onLink: (url: string) => void; onError: (message: string | null) => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<'deadline' | 'release' | null>(null);
  const [dueOn, setDueOn] = useState(ymdOf(dueAt));
  const [grace, setGrace] = useState(String(graceHours ?? 24));
  const [reason, setReason] = useState('');
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['group-hold', id] });
    void queryClient.invalidateQueries({ queryKey: ['group-holds'] });
  };

  const deadline = useMutation({
    mutationFn: () => groupSalesEndpoints.deadline(id, { due_on: dueOn, grace_hours: Number(grace) || 0 }),
    onSuccess: () => {
      onError(null);
      setOpen(null);
      refresh();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });
  const resend = useMutation({
    mutationFn: () => groupSalesEndpoints.resend(id),
    onSuccess: (r) => {
      onError(null);
      onLink(r.url);
      refresh();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });
  const release = useMutation({
    mutationFn: () => groupSalesEndpoints.release(id, reason.trim()),
    onSuccess: () => {
      onError(null);
      setOpen(null);
      refresh();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });

  return (
    <Panel title="What next">
      <div className="space-y-3 px-5 py-4">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setOpen(open === 'deadline' ? null : 'deadline')}>Change the deadline</Button>
          <Button disabled={resend.isPending} onClick={() => resend.mutate()}>
            {resend.isPending ? 'Sending…' : `Email ${contactName.split(' ')[0] || 'the contact'} the link again`}
          </Button>
          <Button onClick={() => navigate(`/group-holds?new=1&from=${id}`)}>Hold more seats for this group</Button>
          {!paid ? (
            <Button variant="danger" onClick={() => setOpen(open === 'release' ? null : 'release')}>
              Release every seat
            </Button>
          ) : null}
        </div>

        {open === 'deadline' ? (
          <form
            className="flex flex-wrap items-end gap-3 rounded border border-line bg-page p-3"
            onSubmit={(e) => {
              e.preventDefault();
              deadline.mutate();
            }}
          >
            <Field label="Pay by" htmlFor="gh-new-due">
              <input id="gh-new-due" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} required className={INPUT} />
            </Field>
            <Field label="Grace (hours)" htmlFor="gh-new-grace">
              <input id="gh-new-grace" value={grace} onChange={(e) => setGrace(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
            </Field>
            <Button type="submit" variant="primary" disabled={deadline.isPending || !dueOn}>
              {deadline.isPending ? 'Saving…' : 'Save'}
            </Button>
            <p className="w-full text-xs text-ink-500">The payment link and its reminders move with it. Reminders already sent stay sent.</p>
          </form>
        ) : null}

        {open === 'release' ? (
          <form
            className="flex flex-wrap items-end gap-3 rounded border border-bad/30 bg-bad-soft p-3"
            onSubmit={(e) => {
              e.preventDefault();
              release.mutate();
            }}
          >
            <Field label="Why" htmlFor="gh-release-reason">
              <input id="gh-release-reason" value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="The trip was canceled" className={`w-80 ${INPUT}`} />
            </Field>
            <Button type="submit" variant="danger" disabled={release.isPending || !reason.trim()}>
              {release.isPending ? 'Releasing…' : 'Release and put the seats back on sale'}
            </Button>
          </form>
        ) : null}
        {paid ? <p className="text-xs text-ink-500">Money has been paid against this hold, so seats come back as a refund from the order, not from here.</p> : null}
      </div>
    </Panel>
  );
}

function SeatsPanel({ id, seats, liveCount, canGiveBack, onError }: { id: string; seats: GroupHoldDetail['seats']; liveCount: number; canGiveBack: boolean; onError: (message: string | null) => void }) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const giveBack = useMutation({
    mutationFn: () => groupSalesEndpoints.giveBack(id, { order_item_ids: [...chosen], reason: reason.trim() }),
    onSuccess: () => {
      onError(null);
      setChosen(new Set());
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['group-hold', id] });
      void queryClient.invalidateQueries({ queryKey: ['group-holds'] });
    },
    onError: (cause: ApiError) => onError(cause.message),
  });
  const toggle = (itemId: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  const all = chosen.size === liveCount && liveCount > 0;

  return (
    <Panel title="Seats" {...(canGiveBack ? { description: 'Tick the seats the group no longer needs to put them back on sale. What they owe goes down with them.' } : {})}>
      <Table headers={[canGiveBack ? '' : ' ', 'Seat', 'Type', 'Price', 'Status']}>
        {seats.map((seat) => {
          const live = seat.status === 'ACTIVE' || seat.status === 'PENDING';
          const label = seatLabel({ section: seat.section, table: seat.table, row: seat.row, seat: seat.seat, ga_area: seat.section ? null : seat.ga_area });
          return (
            <tr key={seat.order_item_id} className={`border-b border-line last:border-0 ${live ? '' : 'text-ink-400'}`}>
              <td className="px-5 py-2 text-sm">{canGiveBack && live ? <input type="checkbox" checked={chosen.has(seat.order_item_id)} onChange={() => toggle(seat.order_item_id)} aria-label={`Give back ${label}`} /> : null}</td>
              <td className="px-5 py-2 text-sm">{label}</td>
              <td className="px-5 py-2 text-sm">{seat.ticket_type ?? '—'}</td>
              <td className="tabular px-5 py-2 text-sm">{dollars(seat.price_cents)}</td>
              <td className="px-5 py-2 text-sm">{live ? 'held' : 'given back'}</td>
            </tr>
          );
        })}
      </Table>
      {canGiveBack && chosen.size > 0 ? (
        <form
          className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            giveBack.mutate();
          }}
        >
          <Field label="Why" htmlFor="gh-giveback-reason">
            <input id="gh-giveback-reason" value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Four students dropped out" className={`w-72 ${INPUT}`} />
          </Field>
          <Button type="submit" variant="danger" disabled={giveBack.isPending || !reason.trim()}>
            {giveBack.isPending ? 'Giving back…' : all ? `Give back all ${chosen.size} and release the hold` : `Give back ${chosen.size} seat${chosen.size === 1 ? '' : 's'}`}
          </Button>
        </form>
      ) : null}
    </Panel>
  );
}
